import React, { useState } from 'react';
import classnames from 'classnames';
import useFetch from 'components/shared/hooks/useFetch';
import { Icon, Card, Spinner } from 'ui';
import FormattedDate from 'components/shared/FormattedDate';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import { routes } from 'constants/config';
import AttemptPreview from './AttemptPreview';

const OnDemandPracticeFeedbackOverview = ({ scenarioId, attemptId }) => {
  const [feedbackIndex, setFeedbackIndex] = useState(0);
  const { data: attemptData, loading: attemptLoading } = useFetch(
    routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.SHOW(scenarioId, attemptId),
  );
  const { data: feedbackData, loading: feedbackLoading } = useFetch(
    routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.ATTEMPT_FEEDBACKS(
      scenarioId,
      attemptId,
    ),
  );
  const ziggeoApiKey = attemptData ? attemptData.ziggeoApiKey : null;

  if (feedbackLoading || attemptLoading) {
    return (
      <div className="mt-24">
        <div className="maxw-layout m-a ff-ss d-f ai-c jc-c">
          <Spinner className="mr-8" /> {t('common.loading')}
        </div>
      </div>
    );
  }

  const { attempt, scenario, hippoPreviewBase } = attemptData;
  const { feedback } = feedbackData;
  const prevButtonDisabled = feedback.length === 1 || feedbackIndex === 0;
  const nextButtonDisabled =
    feedback.length === 1 || feedbackIndex === feedback.length - 1;
  const appreciativeCriteria =
    feedback[feedbackIndex].feedbackCriteria.appreciative || [];
  const criticalCriteria =
    feedback[feedbackIndex].feedbackCriteria.critical || [];

  return (
    <div className="mt-24 p-16">
      <div className="maxw-layout fz-16 m-a ff-ss">
        <a
          className="u-linkBtn mb-24 d-b"
          href={routes.ON_DEMAND_PRACTICE.LEARNING.SHOW(
            scenarioId,
            'previous_responses',
          )}
        >
          <Icon name="back" className="mr-4" />
          {t('practice.practice_feedback.feedback_overview.back_link')}
        </a>
        <Card>
          <Card.Content padding="medium" className="d-f m-fd-c">
            <div className="w-fill mw-25p m-maxw-100p mr-16 m-m-0 mb-16">
              <AttemptPreview
                attempt={attempt}
                ziggeoApiKey={ziggeoApiKey}
                hippoPreviewBase={hippoPreviewBase}
              />
            </div>
            <header className="d-f jc-sb w-fill">
              <div className="mr-16">
                <h3 className="ff-ss fz-16 fw-b m-0 mb-16 text-wrap">
                  {scenario.title}
                </h3>
                <p className="text-wrap">{scenario.prompt}</p>
              </div>
              <div className="ta-r f-sh-0">
                <FormattedDate>{attempt.createdAt}</FormattedDate>
              </div>
            </header>
          </Card.Content>
        </Card>
        <div className="bb-gray-lighter py-16 mb-16">
          <h2 className="ff-ss">
            {t('practice.practice_feedback.feedback_overview.feedback')}
          </h2>
        </div>
        {feedback.length > 0 && (
          <>
            <Card>
              <Card.Content padding="large">
                <div className="ta-c mb-32 mt-24">
                  <strong>
                    {t(
                      'practice.practice_feedback.feedback_overview.feedback_giver',
                    )}
                  </strong>{' '}
                  {feedback[feedbackIndex].feedbackGiver}
                </div>
                <div className="d-f ai-c jc-sb m-fd-c">
                  <div className="d-f ai-c jc-c u-hideBelowXS">
                    <button
                      type="button"
                      disabled={prevButtonDisabled}
                      onClick={() => {
                        setFeedbackIndex(feedbackIndex - 1);
                      }}
                      className="u-resetBtn"
                    >
                      <Icon
                        name="previous"
                        className={classnames('is-32', {
                          'c-gray-dark': prevButtonDisabled,
                        })}
                      />
                    </button>
                  </div>
                  <div className="fg-1 m-w-fill px-32 m-p-0">
                    <div className="d-f mb-32 m-fd-c">
                      <div>
                        <Icon name="color_thumbs_up" className="is-48 mr-8" />
                      </div>
                      <div className="mb-16 w-fill">
                        <strong className="mb-8 d-ib">
                          {t(
                            'practice.practice_feedback.feedback_overview.awesome',
                          )}
                        </strong>
                        <div className="d-f fw-w ai-c mb-16">
                          {appreciativeCriteria.map(criteriaName => (
                            <div className="mr-8 d-ib">
                              <Icon
                                name="check"
                                className="mr-8 c-brand-night"
                              />
                              {criteriaName}
                            </div>
                          ))}
                        </div>
                        <p className="text-wrap">
                          <PreserveFormatting>
                            {feedback[feedbackIndex].appreciativeComment}
                          </PreserveFormatting>
                        </p>
                      </div>
                    </div>
                    <div className="d-f m-fd-c">
                      <div>
                        <Icon name="color_tools" className="is-48 mr-8" />
                      </div>
                      <div className="mb-16 w-fill">
                        <strong className="mb-8 d-ib">
                          {t(
                            'practice.practice_feedback.feedback_overview.improved',
                          )}
                        </strong>
                        <div className="d-f fw-w ai-c mb-16">
                          {criticalCriteria.map(criteriaName => (
                            <div className="mr-8 d-ib">
                              <Icon name="gear" className="mr-8" />
                              {criteriaName}
                            </div>
                          ))}
                        </div>
                        <p className="text-wrap">
                          <PreserveFormatting>
                            {feedback[feedbackIndex].criticalComment}
                          </PreserveFormatting>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="d-f ai-c jc-c u-hideBelowXS">
                    <button
                      type="button"
                      disabled={nextButtonDisabled}
                      onClick={() => {
                        setFeedbackIndex(feedbackIndex + 1);
                      }}
                      className="u-resetBtn"
                    >
                      <Icon
                        name="next"
                        className={classnames('is-32', {
                          'c-gray-dark': nextButtonDisabled,
                        })}
                      />
                    </button>
                  </div>
                </div>
              </Card.Content>
            </Card>
            <div className="d-f ai-c jc-sb u-showBelowXS w-half mx-a mt-32">
              <button
                type="button"
                disabled={prevButtonDisabled}
                onClick={() => {
                  setFeedbackIndex(feedbackIndex - 1);
                }}
                className="u-resetBtn"
              >
                <Icon
                  name="previous"
                  className={classnames('is-32', {
                    'c-gray-dark': prevButtonDisabled,
                  })}
                />
              </button>
              <button
                type="button"
                disabled={nextButtonDisabled}
                onClick={() => {
                  setFeedbackIndex(feedbackIndex + 1);
                }}
                className="u-resetBtn"
              >
                <Icon
                  name="next"
                  className={classnames('is-32', {
                    'c-gray-dark': nextButtonDisabled,
                  })}
                />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default props => <OnDemandPracticeFeedbackOverview {...props} />;
